// @flow

import React from 'react';
import type { ComponentType } from 'react';

type Deprecation = {|
  deprecatedInVersion: number,
  comments?: string,
|};

type RenameDeprecation = {|
  ...Deprecation,
  renameProp: string,
  newProp: string,
|};

type RemoveDeprecation = {|
  ...Deprecation,
  removeProp: string,
|};

type DeprecationType = RenameDeprecation | RemoveDeprecation;

type Config = {|
  deprecations: Array<DeprecationType>,
|};

const withDeprecation = ({
  deprecations = [],
}: Config) => <Props: {}>( // eslint-disable-line arrow-parens
  Component: ComponentType<Props>,
): ComponentType<Props> => {
  const WithDeprecation = (props: Props) => {
    const newProps: Props = deprecations.reduce((accumulate, next) => {
      const warn = (message: string): void => {
        console.warn(`DEPRECATED: ${message} and will be removed in ${Component.displayName || ''} version ${next.deprecatedInVersion} ${next.comments ? `: ${next.comments}` : ''}`);
      };

      // allow dev to rename a prop
      if (next.renameProp && next.renameProp in props) {
        // warn the user
        warn(`${next.renameProp} is deprecated in favor of ${next.newProp}`);
        // cut the original prop
        const { [next.renameProp]: without, ...rest } = accumulate;
        // add the new prop
        return {
          ...rest,
          [next.newProp]: props[next.renameProp],
        };
      }

      // allow dev to remove a prop
      if (next.removeProp && next.removeProp in props) {
        // warn the user
        warn(`${next.removeProp} is deprecated`);
      }
      return accumulate;
    }, { ...props });

    return <Component {...newProps} />;
  };

  WithDeprecation.displayName = `WithDeprecation(${Component.displayName || ''})`;
  return WithDeprecation;
};

export default withDeprecation;
